/**
 * HTTP Error Class
 * 
 * Industry-standard custom error class that preserves HTTP error metadata.
 * Extends Error to maintain stack traces while adding HTTP-specific properties.
 * 
 * This follows the same pattern as AxiosError and other HTTP client libraries.
 */

export class HttpError extends Error {
  public readonly status: number;
  public readonly statusText?: string;
  public readonly data?: any;
  public readonly response?: any;

  constructor(
    message: string,
    status: number,
    options?: {
      statusText?: string;
      data?: any;
      response?: any;
    }
  ) {
    super(message);
    this.name = 'HttpError';
    this.status = status;
    this.statusText = options?.statusText;
    this.data = options?.data;
    this.response = options?.response;

    // Maintains proper stack trace for where our error was thrown (only available on V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, HttpError);
    }

    // Ensure instanceof works correctly
    Object.setPrototypeOf(this, HttpError.prototype);
  }

  /**
   * Check if error is a client error (4xx)
   */
  isClientError(): boolean {
    return this.status >= 400 && this.status < 500;
  }

  /**
   * Check if error is a server error (5xx)
   */
  isServerError(): boolean {
    return this.status >= 500 && this.status < 600;
  }

  /**
   * Check if error is a validation error (400 Bad Request)
   */
  isValidationError(): boolean {
    return this.status === 400;
  }

  /**
   * Check if error is an authentication error (401 Unauthorized)
   */
  isUnauthorized(): boolean {
    return this.status === 401;
  }

  /**
   * Check if error is a forbidden error (403 Forbidden)
   */
  isForbidden(): boolean {
    return this.status === 403;
  }

  /**
   * Check if error is a not found error (404 Not Found)
   */
  isNotFound(): boolean {
    return this.status === 404;
  }

  /**
   * Get error message from response data if available
   */
  getErrorMessage(): string {
    if (this.data?.message) {
      return Array.isArray(this.data.message) 
        ? this.data.message[0] 
        : this.data.message;
    }
    return this.message;
  }
}
